import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Incident {
  id: string
  incident_id: string
  title: string
  incident_type: string
  severity: string
  status: string
  reported_by: string
  reported_date: string
  assigned_to: string
}

interface CorrectiveAction {
  id: string
  incident_id: string
  action_description: string
  assigned_to: string
  status: string
  priority: string
  due_date: string
}

export default function IncidentsPage() {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [actions, setActions] = useState<CorrectiveAction[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [incRes, actRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/incidents/'),
      fetch(API_BASE + '/incidents/corrective-actions'),
      fetch(API_BASE + '/incidents/statistics')
    ])
    if (incRes.ok) setIncidents((await incRes.json()).incidents || [])
    if (actRes.ok) setActions((await actRes.json()).corrective_actions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': case 'closed': case 'completed': return 'bg-green-100 text-green-800'
      case 'open': case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'investigating': case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'escalated': case 'overdue': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Incident Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Report Incident</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Incidents</div>
              <div className="text-2xl font-bold">{statistics.total_incidents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.open_incidents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Critical</div>
              <div className="text-2xl font-bold text-red-600">{statistics.critical_incidents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Resolved MTD</div>
              <div className="text-2xl font-bold text-green-600">{statistics.resolved_this_month}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Resolution</div>
              <div className="text-2xl font-bold">{statistics.avg_resolution_days} days</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Incident Register</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Incident</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {incidents.slice(0, 12).map(inc => (
                  <tr key={inc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{inc.title}</div>
                      <div className="text-xs text-gray-500">{inc.incident_id} • {inc.reported_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm capitalize">{inc.incident_type.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(inc.severity)}`}>{inc.severity}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(inc.status)}`}>{inc.status}</span>
                      <div className="text-xs text-gray-500 mt-1">{inc.assigned_to}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Corrective Actions</h3>
            <div className="space-y-3">
              {actions.slice(0, 10).map(act => (
                <div key={act.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{act.action_description}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(act.status)}`}>{act.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{act.incident_id}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{act.assigned_to}</span>
                    <span className="text-xs text-gray-500">Due: {act.due_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
